/**
 * WizardPopover · v1.0
 *
 * Contextual wizard-style popover that emerges anchored to a click target.
 * Adaptive: 1 step ⇒ hides wizard nav, N steps ⇒ shows progress + Prev/Next.
 *
 * Usage:
 *   const btnRef = useRef();
 *   const [open, setOpen] = useState(false);
 *
 *   <button ref={btnRef} onClick={() => setOpen(true)}>Editar</button>
 *
 *   <WizardPopover
 *     open={open}
 *     onOpenChange={setOpen}
 *     anchorRef={btnRef}
 *     title="Editar Usuario"
 *     subtitle="Ricardo Salazar"
 *     icon={UserCog}
 *     steps={[
 *       { key: "identity", title: "Identidad", icon: User, content: <fields/> },
 *       { key: "role",     title: "Rol",       icon: Shield, content: <fields/> },
 *     ]}
 *     saving={saving}
 *     onSave={handleSave}
 *     onOpenFull={() => openSlideOver()}  // optional
 *   />
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import * as Popover from "@radix-ui/react-popover";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Check, X, Maximize2, Loader2 } from "lucide-react";

export default function WizardPopover({
  open,
  onOpenChange,
  anchorRef,
  title,
  subtitle,
  icon: Icon,
  steps = [],
  saving = false,
  saveLabel = "Guardar",
  onSave,
  onOpenFull,
  extraFooterActions = null,
  width = 460,
  testid = "wizard-popover",
}) {
  const [stepIndex, setStepIndex] = useState(0);
  const isMulti = steps.length > 1;
  // v101.94.0 · El ancla sigue al botón al hacer scroll, cambiar el tamaño de la ventana o el ZOOM del navegador
  const anchorElRef = useRef(null);
  const syncAnchor = () => {
    const el = anchorElRef.current;
    if (!el || !anchorRef?.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    el.style.top = `${r.top}px`;
    el.style.left = `${r.left}px`;
    el.style.width = `${r.width}px`;
    el.style.height = `${r.height}px`;
  };
  useEffect(() => {
    if (!open) return undefined;
    syncAnchor();
    window.addEventListener("resize", syncAnchor);
    window.addEventListener("scroll", syncAnchor, true);
    return () => {
      window.removeEventListener("resize", syncAnchor);
      window.removeEventListener("scroll", syncAnchor, true);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset step when opening
  useEffect(() => {
    if (open) setStepIndex(0);
  }, [open]);

  const current = steps[stepIndex] || { content: null };
  const canPrev = stepIndex > 0;
  const canNext = stepIndex < steps.length - 1;

  const handleNext = () => {
    if (canNext) setStepIndex(stepIndex + 1);
    else onSave?.();
  };

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      {/* Anchor is attached externally via ref — we render a hidden invisible anchor */}
      <Popover.Anchor asChild>
        <span
          style={{ position: "fixed", pointerEvents: "none" }}
          ref={(el) => {
            anchorElRef.current = el;
            syncAnchor();
          }}
        />
      </Popover.Anchor>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={10}
          collisionPadding={16}
          className="wizard-popover-content"
          style={{ width, maxWidth: "calc(100vw - 1rem)", zIndex: 60 }}
          data-testid={testid}
          onOpenAutoFocus={(e) => e.preventDefault()}
          asChild
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: -8 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="wizard-popover-card"
          >
            {/* ─── HEADER ───────────────────────────────────────── */}
            <header className="wizard-popover-header">
              <div className="flex items-start gap-2.5 min-w-0 flex-1">
                {Icon && (
                  <div className="wizard-popover-icon">
                    <Icon size={16} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <h3 className="wizard-popover-title" data-testid={`${testid}-title`}>
                    {title}
                  </h3>
                  {subtitle && (
                    <p className="wizard-popover-subtitle" data-testid={`${testid}-subtitle`}>
                      {subtitle}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {onOpenFull && (
                  <button
                    type="button"
                    onClick={onOpenFull}
                    className="wizard-popover-iconbtn"
                    title="Ver detalle completo"
                    data-testid={`${testid}-open-full`}
                  >
                    <Maximize2 size={13} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onOpenChange?.(false)}
                  className="wizard-popover-iconbtn"
                  title="Cerrar"
                  data-testid={`${testid}-close`}
                >
                  <X size={14} />
                </button>
              </div>
            </header>

            {/* ─── STEP TABS (only when multi-step) ─────────────── */}
            {isMulti && (
              <nav className="wizard-popover-steps" data-testid={`${testid}-steps`}>
                {steps.map((s, i) => {
                  const StepIcon = s.icon;
                  const done = i < stepIndex;
                  const active = i === stepIndex;
                  return (
                    <button
                      key={s.key || i}
                      type="button"
                      onClick={() => setStepIndex(i)}
                      className={`wizard-popover-step ${active ? "is-active" : ""} ${done ? "is-done" : ""}`}
                      data-testid={`${testid}-step-${i}`}
                    >
                      <span className="wizard-popover-step-dot">
                        {done ? <Check size={10} strokeWidth={3} /> : StepIcon ? <StepIcon size={11} /> : i + 1}
                      </span>
                      <span className="wizard-popover-step-label">{s.title}</span>
                    </button>
                  );
                })}
              </nav>
            )}

            {/* ─── BODY ─────────────────────────────────────────── */}
            <div className="wizard-popover-body" data-testid={`${testid}-body`}>
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={current.key || stepIndex}
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -12 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                >
                  {current.content}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* ─── FOOTER ───────────────────────────────────────── */}
            <footer className="wizard-popover-footer">
              {isMulti && (
                <div className="wizard-popover-progress">
                  <span className="wizard-popover-progress-text">
                    Paso {stepIndex + 1} de {steps.length}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 ml-auto">
                {extraFooterActions}
                {isMulti && canPrev && (
                  <button
                    type="button"
                    onClick={() => setStepIndex(stepIndex - 1)}
                    className="wizard-popover-btn-ghost"
                    data-testid={`${testid}-prev`}
                  >
                    <ChevronLeft size={14} />
                    Atrás
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={saving}
                  className="wizard-popover-btn-primary"
                  data-testid={`${testid}-next`}
                >
                  {saving ? (
                    <>
                      <Loader2 size={13} className="animate-spin" />
                      Guardando…
                    </>
                  ) : canNext ? (
                    <>
                      Siguiente
                      <ChevronRight size={14} />
                    </>
                  ) : (
                    <>
                      <Check size={14} />
                      {saveLabel}
                    </>
                  )}
                </button>
              </div>
            </footer>
          </motion.div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
